import { useState, useRef } from "react";
import { Mail, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Turnstile } from "react-turnstile";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const NewsletterSignup = ({ variant = "default" }) => {
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !agreed) {
      toast.error("Bitte E-Mail eingeben und Datenschutz akzeptieren.");
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Bitte bestätigen Sie, dass Sie kein Roboter sind.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        privacy_accepted: agreed,
        turnstile_token: turnstileToken
      });
      setSuccess(true);
      toast.success("Bestätigungs-E-Mail wurde gesendet!");
    } catch (error) {
      toast.error("Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className={`rounded-xl p-8 text-center ${
          variant === "compact" ? "bg-monaco-bg-2" : "bg-monaco-bg-1 border border-white/5"
        }`}
        data-testid="newsletter-success"
      >
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="font-display text-xl text-monaco-text-0 mb-2">
          Fast geschafft!
        </h3>
        <p className="text-monaco-text-1 text-sm">
          Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. 
          Bitte bestätigen Sie Ihre Anmeldung.
        </p>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-xl ${
        variant === "compact" 
          ? "bg-monaco-bg-2 p-6" 
          : "bg-monaco-bg-1 border border-white/5 p-8"
      }`}
      data-testid="newsletter-signup"
    >
      {variant !== "compact" && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-monaco-red/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-monaco-red" />
            </div>
            <h3 className="font-display text-xl text-monaco-text-0">
              Newsletter
            </h3>
          </div>
          <p className="text-monaco-text-1 text-sm mb-6">
            Erhalten Sie 2× pro Woche exklusive Updates über unsere neuesten Girls.
          </p>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-3">
          <Input
            type="email"
            placeholder="Ihre E-Mail Adresse"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-monaco-bg-0 border-white/10 text-monaco-text-0 placeholder:text-monaco-text-2"
            data-testid="newsletter-email"
          />
          <Button
            type="submit"
            disabled={loading || !agreed}
            className="bg-monaco-red hover:bg-monaco-red-hover text-white btn-shine relative overflow-hidden disabled:opacity-50"
            data-testid="newsletter-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Anmelden"
            )}
          </Button>
        </div>

        {TURNSTILE_SITE_KEY && (
          <div data-testid="newsletter-turnstile" style={{ transform: 'scale(0.85)', transformOrigin: 'left top', marginBottom: '-8px' }}>
            <Turnstile
              ref={turnstileRef}
              sitekey={TURNSTILE_SITE_KEY}
              onVerify={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              theme="dark"
            />
          </div>
        )}

        <div className="flex items-start gap-2">
          <Checkbox
            id="newsletter-privacy"
            checked={agreed}
            onCheckedChange={setAgreed}
            className="mt-0.5 border-white/20 data-[state=checked]:bg-monaco-red data-[state=checked]:border-monaco-red"
            data-testid="newsletter-checkbox"
          />
          <label htmlFor="newsletter-privacy" className="text-xs text-monaco-text-2 cursor-pointer">
            Ich akzeptiere die{" "}
            <a href="/datenschutz" className="text-monaco-red hover:underline">
              Datenschutzbestimmungen
            </a>{" "}
            und möchte den Newsletter erhalten.
          </label>
        </div>
      </form>
    </div>
  );
};

export default NewsletterSignup;
